import { NextPage } from "next";
import React, { ReactNode, useEffect, useState } from "react";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isProd } from "@/Env";
import { isInAppWebView } from "@/components/webview/utils/webviewUserAgnetUtils";
import { GlobalRouterLoadingPcpWebView } from "@/components/webview/shared/GlobalRouterLoadingPcpWebView";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactNode) => ReactNode;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

/*
 pcp - web view layout hoc
 */
export function withWebViewPcpLayout<P extends object>(
  webViewComponents: NextPage<P>,
): NextPageWithLayout {
  const ComponentWithLayout = webViewComponents as NextPageWithLayout;
  ComponentWithLayout.getLayout = (page) => {
    return (
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary
          state={(page as any)?.props?.pageProps?.dehydratedState}
        >
          <WebViewGuard>{page}</WebViewGuard>
          <GlobalRouterLoadingPcpWebView />
          <ReactQueryDevtools />
        </HydrationBoundary>
      </QueryClientProvider>
    );
  };
  return ComponentWithLayout;
}

/*
 webView 환경이 아니면 initUrl 리다이렉트
 */
function WebViewGuard({ children }: { children: ReactNode }) {
  const [allowed, setAllowed] = useState(false);
  const initUrl = isProd
    ? "https://www.bhaptics.com/"
    : "https://dev.bhaptics.com/";

  useEffect(() => {
    // dev 에선 실행 하도록
    if (isInAppWebView() || !isProd) {
      setAllowed(true);
    } else {
      window.location.href = initUrl;
    }
    document.body.style.backgroundColor = "#000000"; //  webview - background webview
    document.body.style.userSelect = "none"; // webview - Disable text selection
    document.body.style.padding = "12px 12px"; // webview - Disable text selection
    document.body.style.overflowX = "hidden"; // webview - Disable scroll
  }, []);

  if (!allowed) {
    return null;
  }

  return <div className="mb-5 rounded-xl bg-[#0E0E0E] px-3">{children}</div>;
}
